'use client'

import React, { useState, useEffect, useCallback } from 'react'
import { Clock, CheckCircle, AlertTriangle, Maximize2, X, Flag, ChevronDown, ChevronRight, ImageIcon, Save, Trash2 } from 'lucide-react'
import { postAws } from '../aws'
import { useNavigate } from 'react-router-dom'
import { generatePresignedUrl, postStudentExmFile } from '../postFileAws'

// Updated exam data with the new sections
const Loader = () => (
  <div className="flex justify-center items-center h-[120px] w-[100px]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
  </div>
)

export default function ExamPortal() {
  const [currentSection, setCurrentSection] = useState(0)
  const [currentQuestion, setCurrentQuestion] = useState(0)
  const [tempAnswers, setTempAnswers] = useState({})
  const [savedAnswers, setSavedAnswers] = useState({})
  const [timeLeft, setTimeLeft] = useState(3600) // 1 hour in seconds
  const [isFullScreen, setIsFullScreen] = useState(false)
  const [showFullScreenAlert, setShowFullScreenAlert] = useState(false)
  const [showSubmitConfirmation, setShowSubmitConfirmation] = useState(false)
  const [questionStatus, setQuestionStatus] = useState({})
  const [showThankYouPopup, setShowThankYouPopup] = useState(false)
  const [collapsedSections, setCollapsedSections] = useState({})
  const [tempUserImages, setTempUserImages] = useState({})
  const [savedUserImages, setSavedUserImages] = useState({})
  const [imageUrls, setImageUrls] = useState({})
  const [imageLoading, setImageLoading] = useState({})
  const [examFetchData, setExamFetchData] = useState();
  const [examData, setExamData ] = useState({ sections: [
    {
      name: "Section 1",
      questions: [
        {
          text: "acha",
          type: "multiple-choice",
          options: ["az", "dx", "c", "c"],
          marks: 1,
          image: null,
          _id: "676bbf4a57f5babaeef34450"
        }
      ],
      _id: "676bbf4a57f5babaeef3444f"
    } ]});

  const navigate = useNavigate();  
  const localExamData = localStorage.getItem("examData");

  useEffect(() => {
    if(!examFetchData){
      const examFetch = JSON.parse(localExamData)
      setExamFetchData(examFetch);
      setExamData({ sections: examFetch.sections })
    }
  }, [examFetchData, localExamData])

  const enterFullScreen = useCallback(() => {
    const element = document.documentElement;
    const requestMethod = element.requestFullscreen || element.webkitRequestFullscreen || element.mozRequestFullScreen || element.msRequestFullscreen;
    
    if (requestMethod) {
      requestMethod.call(element).then(() => {
        setIsFullScreen(true);
        setShowFullScreenAlert(false);
      }).catch(err => {
        console.error(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
        setIsFullScreen(false);
        setShowFullScreenAlert(true);
      });
    } else {
      console.warn("Fullscreen API is not supported.");
      setIsFullScreen(false);
      setShowFullScreenAlert(true);
    }
  }, []);

  useEffect(() => {
    // Start the exam immediately
    enterFullScreen();
    
    const timer = setInterval(() => {
      setTimeLeft((prevTime) => (prevTime > 0 ? prevTime - 1 : 0))
    }, 1000)

    return () => clearInterval(timer)
  }, [enterFullScreen])

  useEffect(() => {
    const handleFullScreenChange = () => {
      const isFullScreen = !!document.fullscreenElement;
      setIsFullScreen(isFullScreen);
      if (!isFullScreen) {
        setShowFullScreenAlert(true);
      }
    };

    document.addEventListener('fullscreenchange', handleFullScreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullScreenChange);
  }, []);

  const handleTempAnswer = (answer, questionId) => {
    const question = examData.sections[currentSection].questions[currentQuestion];
    if (question.type === 'multiple-choice') {
      setTempAnswers(prev => {
        const currentAnswers = prev[questionId] || []
        const updatedAnswers = currentAnswers.includes(answer)
          ? currentAnswers.filter(a => a !== answer)
          : [...currentAnswers, answer]
        return { ...prev, [questionId]: updatedAnswers }
      })
    } else {
      setTempAnswers(prev => ({ ...prev, [questionId]: answer }))
    }
  }

  const handleImageUpload = async (event) => {
    const file = event.target.files[0]
    if (file) {
      const questionId = examData.sections[currentSection].questions[currentQuestion]._id
      
      const studentId = localStorage.getItem("studentId")
      const saveFileS3 = await postStudentExmFile(file, studentId, examFetchData._id, questionId)
      
      const filePreSignedUrl = await generatePresignedUrl(saveFileS3, Number(examFetchData.examDuration)*60);
      setTempUserImages({ ...tempUserImages, [questionId]: saveFileS3 })
      setImageLoading(prev => ({ ...prev, [questionId]: true }))
  
      // Create object URL for the image preview
      const imageUrl = URL.createObjectURL(file)
      const img = new Image()
      img.onload = () => {
        setImageUrls(prev => ({ ...prev, [questionId]: filePreSignedUrl }))
        setImageLoading(prev => ({ ...prev, [questionId]: false }))
      }
      img.src = imageUrl

      // Store the image name in the user_image field
      examData.sections[currentSection].questions[currentQuestion].user_image = saveFileS3

      console.log(`Image ${file.name} selected for question ${questionId}`)
    }
  }

  const handleSave = () => {
    const questionId = examData.sections[currentSection].questions[currentQuestion]._id
    let saved = false;
    
    // Save answer
    if (tempAnswers[questionId] !== undefined) {
      setSavedAnswers(prev => ({ ...prev, [questionId]: tempAnswers[questionId] }))
      const question = examData.sections[currentSection].questions[currentQuestion]
      if (question.type === 'multiple-choice') {
        const selectedOptions = tempAnswers[questionId].join(', ')
        question.user_answer = `Selected options: ${selectedOptions}`
      } else {
        question.user_answer = tempAnswers[questionId]
      }
      saved = true;
    }
    
    // Save image
    if (tempUserImages[questionId]) {
      setSavedUserImages(prev => ({ ...prev, [questionId]: tempUserImages[questionId] }))
      const imageName = tempUserImages[questionId]
      examData.sections[currentSection].questions[currentQuestion].user_image = imageName
    
      saved = true;
    }
    
    if (saved) {
      setQuestionStatus(prev => ({ ...prev, [questionId]: 'answered' }))
      console.log('Answer and/or image saved for question', questionId)
    } else {
      console.log('No changes to save for question', questionId)
    }
  }

  const handleNavigation = (sectionIndex, questionIndex) => {
    setCurrentSection(sectionIndex)
    setCurrentQuestion(questionIndex)
    const questionId = examData.sections[sectionIndex].questions[questionIndex]._id
    setQuestionStatus(prevStatus => ({
      ...prevStatus,
      [questionId]: prevStatus[questionId] || 'viewed'
    }))
  }

  const handleMarkForReview = () => {
    const questionId = examData.sections[currentSection].questions[currentQuestion]._id
    setQuestionStatus({ ...questionStatus, [questionId]: 'review' })
  }

  const handleSubmit = () => {
    setShowSubmitConfirmation(true)
  }

  const confirmSubmit = async () => {
    console.log('Submitting exam data:', examData)
    setShowThankYouPopup(true)

    const examString = await localStorage.getItem("examData");
    const examValues = JSON.parse(examString);
    const studentString = await localStorage.getItem("studentData");
    const studentValues = JSON.parse(studentString);

    const submissionData = {
      body:{
        examId: examValues._id,
        exam_topic: examValues.exam_topic,
        teacherId: examValues.teacherId,
        studentId: studentValues._id,
        studentName: studentValues.studentName,
        examQuestion: examData.sections,
        examStartDate: examValues.examStartDate,
        examEndDate: examValues.examEndDate,
        examDuration: examValues.examDuration,
        obtainedMarks: 0,
        status: "submitted"
      },
      request:"submit-Exam"
    }

    const response = await postAws('https://api-dev.aurait.ai/examSubmission', "POST", submissionData);


    if(response.statusCode == 200){
      navigate("/exams")
    }else{
      alert(response.body);
    }

    setShowThankYouPopup(false)

  }

  const handleReset = () => {
    const questionId = examData.sections[currentSection].questions[currentQuestion]._id
    setTempAnswers(prev => ({ ...prev, [questionId]: '' }))
    setTempUserImages(prev => ({ ...prev, [questionId]: null }))
    setImageUrls(prev => ({ ...prev, [questionId]: null }))
    
    // Clear user_answer and user_image in examData
    const question = examData.sections[currentSection].questions[currentQuestion]
    if (question.user_answer) {
      delete question.user_answer
    }
    if (question.user_image) {
      delete question.user_image
    }
    
    console.log('Answer and image reset for question', questionId)
  }

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  const getQuestionColor = (questionId) => {
    if (questionStatus[questionId] === 'review') return 'bg-yellow-200 text-yellow-800'
    if (questionStatus[questionId] === 'answered') return 'bg-green-200 text-green-800'
    if (questionStatus[questionId] === 'viewed') return 'bg-red-200 text-red-800'
    return 'bg-gray-100 text-gray-800 hover:bg-gray-200'
  }

  const toggleSection = (sectionId) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }))
  }

  const renderQuestion = () => {
    const question = examData.sections[currentSection].questions[currentQuestion]
    const questionId = question._id

    return (
      <div className="bg-white shadow-lg rounded-lg p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-2 text-indigo-700">
          Question {currentQuestion + 1}
        </h2>
        <p className="mb-2 text-sm text-gray-500">Marks: {question.marks}</p>
        <p className="mb-6 text-lg text-gray-700">{question.text}</p>

        {question.type === 'multiple-choice' && (
          <div className="space-y-4">
            {question.options.map((option, index) => (
              <label key={index} className="flex items-center space-x-3 p-4 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all duration-200 cursor-pointer">
                <input
                  type="checkbox"
                  name={`question-${questionId}`}
                  value={option}
                  checked={(tempAnswers[questionId] || []).includes(option)}
                  onChange={() => handleTempAnswer(option, questionId)}
                  className="form-checkbox h-5 w-5 text-indigo-600"
                />
                <span className="text-gray-700 text-lg">{option}</span>
              </label>
            ))}
          </div>
        )}

        {(question.type === 'short-answer' || question.type === 'essay') && (
          <>
            <textarea
              value={tempAnswers[questionId] || ''}
              onChange={(e) => handleTempAnswer(e.target.value, questionId)}
              className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg mb-6"
              rows={question.type === 'essay' ? 10 : 4}
              placeholder={`Enter your ${question.type === 'essay' ? 'essay' : 'answer'} here`}
            />
            <div className="mt-4">
              <label htmlFor={`image-upload-${questionId}`} className="flex items-center space-x-2 cursor-pointer">
                <div className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all duration-200">
                  <ImageIcon className="w-5 h-5 inline-block mr-2" />
                  <span>{tempUserImages[questionId] ? 'Change Image' : 'Add Image'}</span>
                </div>
                <span className="text-sm text-gray-500">
                  {tempUserImages[questionId] ? tempUserImages[questionId].name : 'No image selected'}
                </span>
              </label>
              <input
                id={`image-upload-${questionId}`}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              {(imageLoading[questionId] || imageUrls[questionId]) && (
                <div className="mt-2">
                  {imageLoading[questionId] ? (
                    <Loader />
                  ) : (
                    <img 
                      src={imageUrls[questionId]} 
                      alt="Uploaded image preview" 
                      className="w-[100px] h-[120px] object-cover rounded-lg border border-gray-300"
                    />
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    )
  }

  useEffect(() => {
    return () => {
      // Clean up object URLs when component unmounts
      Object.values(imageUrls).forEach(URL.revokeObjectURL)
    }
  }, [imageUrls])

  return (
    <>
      <div className={`flex h-screen bg-gradient-to-br from-blue-50 to-indigo-100 ${!isFullScreen ? 'blur-sm' : ''}`}>
        {/* Sidebar */}
        <div className="w-72 bg-white shadow-lg overflow-y-auto">
          <div className="p-6">
            <h2 className="text-2xl font-bold mb-6 text-indigo-800">Sections</h2>
            {examData.sections.map((section, sectionIndex) => (
              <div key={section._id} className="mb-6">
                <button
                  onClick={() => toggleSection(section._id)}
                  className="flex items-center justify-between w-full text-lg font-semibold mb-2 text-indigo-600 hover:text-indigo-800 transition-colors duration-200"
                >
                  <span>{section.name}</span>
                  {collapsedSections[section._id] ? (
                    <ChevronRight className="w-5 h-5" />
                  ) : (
                    <ChevronDown className="w-5 h-5" />
                  )}
                </button>
                {!collapsedSections[section._id] && (
                  <div className="grid grid-cols-3 gap-2">
                    {section.questions.map((question, questionIndex) => (
                      <button
                        key={question._id}
                        onClick={() => handleNavigation(sectionIndex, questionIndex)}
                        className={`p-2 text-center rounded-lg transition-all duration-200 ${
                          currentSection === sectionIndex && currentQuestion === questionIndex
                            ? 'bg-indigo-600 text-white shadow-md'
                            : getQuestionColor(question._id)
                        }`}
                      >
                        {questionIndex + 1}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="mt-6 text-sm">
              <div className="flex items-center mb-2">
                <div className="w-3 h-3 bg-gray-100 border border-gray-300 mr-2"></div>
                <span>Unvisited</span>
              </div>
              <div className="flex items-center mb-2">
                <div className="w-3 h-3 bg-red-200 border border-red-300 mr-2"></div>
                <span>Visited</span>
              </div>
              <div className="flex items-center mb-2">
                <div className="w-3 h-3 bg-green-200 border border-green-300 mr-2"></div>
                <span>Answered</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-yellow-200 border border-yellow-300 mr-2"></div>
                <span>Marked for Review</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 p-8 overflow-y-auto">
          <div className="mb-8 flex justify-between items-center bg-white rounded-lg shadow-md p-6">
            <h1 className="text-3xl font-bold text-indigo-800">Exam Portal</h1>
            <div className="flex items-center space-x-4 text-indigo-600">
              <Clock className="w-6 h-6" />
              <span className="text-2xl font-semibold">{formatTime(timeLeft)}</span>
            </div>
          </div>

          {renderQuestion()}

          <div className="flex justify-between items-center">
            <button
              onClick={() => handleNavigation(currentSection, currentQuestion - 1)}
              disabled={currentQuestion === 0 && currentSection === 0}
              className="bg-gray-200 text-gray-700 px-6 py-3 rounded-lg flex items-center hover:bg-gray-300 transition-all duration-200 disabled:opacity-50 text-lg"
            >
              Previous
            </button>
            <div className="flex space-x-4">
              <button
                onClick={handleReset}
                className="bg-red-100 text-red-700 px-6 py-3 rounded-lg flex items-center hover:bg-red-200 transition-all duration-200 text-lg font-semibold"
              >
                <Trash2 className="w-5 h-5 mr-2" />
                Reset
              </button>
              <button
                onClick={handleMarkForReview}
                className="bg-white border-2 border-yellow-500 text-yellow-500 px-6 py-3 rounded-lg flex items-center hover:bg-yellow-500 hover:text-white transition-all duration-200 text-lg font-semibold"
              >
                <Flag className="w-5 h-5 mr-2" />
                Mark for Review
              </button>
              <button
                onClick={handleSave}
                className="bg-green-100 text-green-700 px-6 py-3 rounded-lg flex items-center hover:bg-green-200 transition-all duration-200 text-lg font-semibold"
              >
                <Save className="w-5 h-5 mr-2" />
                Save
              </button>
            </div>
            <button
              onClick={() => {
                const nextSection = currentSection
                const nextQuestion = currentQuestion + 1
                if (nextQuestion < examData.sections[nextSection].questions.length) {
                  handleNavigation(nextSection, nextQuestion)
                } else if (nextSection < examData.sections.length - 1) {
                  handleNavigation(nextSection + 1, 0)
                }
              }}
              disabled={currentSection === examData.sections.length - 1 && currentQuestion === examData.sections[examData.sections.length - 1].questions.length - 1}
              className="bg-indigo-600 text-white px-6 py-3 rounded-lg flex items-center hover:bg-indigo-700 transition-all duration-200 disabled:opacity-50 text-lg"
            >
              Next
            </button>
          </div>

          <button
            onClick={handleSubmit}
            className="mt-8 bg-gradient-to-r from-red-500 to-red-600 text-white px-8 py-4 rounded-full font-bold flex items-center justify-center w-full hover:from-red-600 hover:to-red-700 transition-all duration-200 text-xl shadow-lg"
          >
            <CheckCircle className="w-6 h-6 mr-2" />
            Submit Exam
          </button>
        </div>
      </div>

      {/* Full-screen alert */}
      {showFullScreenAlert && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-lg shadow-xl max-w-md">
            <div className="flex items-center mb-4 text-yellow-600">
              <AlertTriangle className="w-8 h-8 mr-2" />
              <h2 className="text-2xl font-bold">Full-screen Required</h2>
            </div>
            <p className="mb-6 text-gray-700">
              Full-screen mode is required for the exam. Please enter full-screen mode to continue.
            </p>
            <div className="flex justify-center">
              <button
                onClick={enterFullScreen}
                className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all duration-200 text-lg font-semibold"
              >
                Enter Full-screen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Submit confirmation modal */}
      {showSubmitConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-lg shadow-xl max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-indigo-800">Confirm Submission</h2>
              <button onClick={() => setShowSubmitConfirmation(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>
            <p className="mb-6 text-gray-700">
              Are you sure you want to submit your exam? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowSubmitConfirmation(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmSubmit}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all duration-200"
              >
                Confirm Submission
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Thank you popup */}
      {showThankYouPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-lg shadow-xl max-w-md text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-indigo-800 mb-4">Thank You!</h2>
            <p className="text-gray-700">
              Your exam has been submitted successfully. You will be redirected shortly.
            </p>
          </div>
        </div>
      )}
    </>
  )
}

